import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, CircleDot, Bot } from 'lucide-react';
import { RecipeWithStats } from '@/services/recipe/recipeStatsService';
import RecipeImage from '@/components/recipe/RecipeImage';
import RecipeIcon from '@/components/common/RecipeIcon';
import { formatDuration } from '@/utils/timeUtils';

interface RecipeImageSelectionCardProps {
  recipe: RecipeWithStats;
  onSelect: () => void;
  isSubmitting: boolean;
  onTagSelect?: (tag: string) => void;
}

const RecipeImageSelectionCard: React.FC<RecipeImageSelectionCardProps> = ({ 
  recipe, 
  onSelect, 
  isSubmitting, 
  onTagSelect 
}) => {
  const getUsageBadgeVariant = () => {
    if (!recipe.usageCount || recipe.usageCount === 0) return "secondary";
    if (recipe.usageCount >= 3) return "default";
    return "outline";
  };

  // Create unified tags array - same logic as RecipeCard
  const allTags = [];

  // Add quick meal tag
  if (recipe.isQuickRecipe) {
    allTags.push({
      type: 'quick-meal',
      content: (
        <Badge key="quick-meal" variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
          <RecipeIcon type="quick-recipe" size={10} className="mr-1" />
          Quick meal
        </Badge>
      )
    });
  }

  // Add AI suggestion tag
  if (recipe.suggestion_id) {
    allTags.push({
      type: 'ai-suggestion',
      content: (
        <Badge key="ai-suggestion" variant="outline" className="text-xs border-violet-300 text-violet-700 bg-violet-50">
          <Bot className="h-3 w-3 mr-1" />
          AI-suggestion
        </Badge>
      )
    });
  }

  // Add usage statistics tag
  if (recipe.usageCount !== undefined) {
    if (recipe.usageCount > 0) {
      allTags.push({
        type: 'usage',
        content: (
          <Badge key="usage" variant={getUsageBadgeVariant()} className="text-xs">
            <TrendingUp className="h-3 w-3 mr-1" />
            Used {recipe.usageCount}x
          </Badge>
        )
      });
    } else {
      allTags.push({
        type: 'usage',
        content: (
          <Badge key="never-used" variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">
            <CircleDot className="h-3 w-3 mr-1" />
            Never used
          </Badge>
        )
      });
    }
  }

  // Add regular tags (clickable if onTagSelect provided)
  if (recipe.tags && recipe.tags.length > 0) {
    recipe.tags.forEach((tag, index) => {
      allTags.push({
        type: 'regular',
        content: (
          <Badge 
            key={`tag-${index}`} 
            variant="secondary" 
            className={`text-xs bg-golden-light text-charcoal border-golden ${onTagSelect ? 'cursor-pointer hover:bg-golden hover:text-cream transition-colors' : ''}`}
            onClick={() => onTagSelect?.(tag)}
          >
            {tag}
          </Badge>
        )
      });
    });
  }

  return (
    <Card className="h-full flex flex-col border-golden-light bg-card shadow-sm hover:shadow-md transition-shadow duration-200 hover:border-sage/30">
      <div className="relative">
        <RecipeImage 
          recipe={recipe}
          size="md"
          className="w-full rounded-t-lg"
        />
        {recipe.isQuickRecipe && (
          <div className="absolute top-2 right-2 bg-emerald-100 rounded-full p-1">
            <RecipeIcon type="quick-recipe" size={12} className="text-emerald-700" />
          </div>
        )}
      </div>
      
      <CardHeader className="pb-2">
        <CardTitle className="line-clamp-2 text-base md:text-lg font-playfair text-charcoal">
          {recipe.title}
        </CardTitle>
        {!recipe.isQuickRecipe && (
          <div className="text-xs md:text-sm text-charcoal/70 font-inter">
            <span className="mr-3">Prep: {formatDuration(recipe.prepTime, true)}</span>
            <span>Cook: {formatDuration(recipe.cookTime, true)}</span>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="flex-1">
        <p className="text-sm text-muted-foreground line-clamp-3 font-inter">
          {recipe.description || 'No description available.'}
        </p>
        
        {/* Unified Tags Display */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {allTags.map((tag, index) => (
              <div key={index}>
                {tag.content}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="pt-2">
        <Button 
          onClick={onSelect}
          disabled={isSubmitting}
          size="sm" 
          className="w-full h-9 md:h-8 text-sm font-inter bg-sage text-cream hover:bg-sage/90"
        >
          {isSubmitting ? 'Selecting...' : 'Select Recipe'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RecipeImageSelectionCard;